/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC6,
	ResumeIndicatorC6Data,
} from "@/services/esus/indicator/esf/c6/getResumeIndicatorC6";
import {
	getResumeIndicatorC6ByHealthTeam,
	ResumeIndicatorC6ByHealthTeamData,
} from "@/services/esus/indicator/esf/c6/getResumeIndicatorC6ByHealthTeam";
import {
	DetailedCitizenScoresIndicatorC6Data,
	getDetailedCitizensScoresIndicatorC6,
} from "@/services/esus/indicator/esf/c6/getDetailedCitizensScoresIndicatorC6";
import {
	DetailedCitizenAcsVisitsIndicatorC6Data,
	getDetailedCitizensAcsVisitsIndicatorC6,
} from "@/services/esus/indicator/esf/c6/getDetailedCitizensAcsVisitsIndicatorC6";
import {
	DetailedCitizenAttendancesIndicatorC6Data,
	getDetailedCitizensAttendancesIndicatorC6,
} from "@/services/esus/indicator/esf/c6/getDetailedCitizensAttendancesIndicatorC6";
import {
	DetailedCitizenVaccinationsIndicatorC6Data,
	getDetailedCitizensVaccinationsIndicatorC6,
} from "@/services/esus/indicator/esf/c6/getDetailedCitizensVaccinationsIndicatorC6";

import { IndicatorEsfC6Resume } from "./IndicatorEsfC6Resume";
import { IndicatorEsfC6CitizensAttendances } from "./IndicatorEsfC6CitizensAttendances";
import { IndicatorEsfC6HealthTeams } from "./IndicatorEsfC6HealthTeams";
import { IndicatorEsfC6CitizensScores } from "./IndicatorEsfC6CitizensScores";
import { IndicatorEsfC6CitizensAcsVisits } from "./IndicatorEsfC6CitizensAcsVisits";
import { IndicatorEsfC6CitizensVaccinations } from "./IndicatorEsfC6CitizensVaccinations";

type IndicatorEsfC6Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC6({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC6Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC6, setResumeIndicatorC6] = useState<ResumeIndicatorC6Data>(
		new ResumeIndicatorC6Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC6ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizensScores, setPageNumberCitizensScores] = useState(1);
	const [searchCitizensScore, setSearchCitizensScore] = useState("");
	const [isEvaluatedCitizensScore, setIsEvaluatedCitizensScore] = useState<boolean>();
	const debouncedSearchCitizensScore = useDebounce(searchCitizensScore, 2000);
	const [paginatedCitizensScores, setPaginatedCitizensScores] = useState(
		new Paginated<DetailedCitizenScoresIndicatorC6Data>()
	);
	const [isLoadingCitizensScores, setIsLoadingCitizensScores] = useState(false);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchCitizensWithAttendance, setSearchCitizensWithAttendance] = useState("");
	const [isEvaluatedCitizensWithAttendance, setIsEvaluatedCitizensWithAttendance] =
		useState<boolean>();
	const debouncedSearchAttendance = useDebounce(searchCitizensWithAttendance, 2000);
	const [paginatedCitizensWithAttendances, setPaginatedCitizensWithAttendances] = useState(
		new Paginated<DetailedCitizenAttendancesIndicatorC6Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberCitizensAcsVisits, setPageNumberCitizensAcsVisits] = useState(1);
	const [searchCitizensAcsVisit, setSearchCitizensAcsVisit] = useState("");
	const [isEvaluatedCitizensAcsVisit, setIsEvaluatedCitizensAcsVisit] = useState<boolean>();
	const debouncedSearchCitizensAcsVisit = useDebounce(searchCitizensAcsVisit, 2000);
	const [paginatedCitizensAcsVisits, setPaginatedCitizensAcsVisits] = useState(
		new Paginated<DetailedCitizenAcsVisitsIndicatorC6Data>()
	);
	const [isLoadingCitizensAcsVisits, setIsLoadingCitizensAcsVisits] = useState(false);

	const [pageNumberCitizensVaccinations, setPageNumberCitizensVaccinations] = useState(1);
	const [searchCitizensVaccination, setSearchCitizensVaccination] = useState("");
	const [isEvaluatedCitizensVaccination, setIsEvaluatedCitizensVaccination] = useState<boolean>();
	const debouncedSearchCitizensVaccination = useDebounce(searchCitizensVaccination, 2000);
	const [paginatedCitizensVaccinations, setPaginatedCitizensVaccinations] = useState(
		new Paginated<DetailedCitizenVaccinationsIndicatorC6Data>()
	);
	const [isLoadingCitizensVaccinations, setIsLoadingCitizensVaccinations] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC6(),
			handleGetResumeIndicatorC6ByHealthTeam(),
			handleGetDetailedCitizensScoresIndicatorC6(),
			handleGetDetailedCitizensWithAttendancesIndicatorC6(),
			handleGetDetailedCitizensAcsVisitsIndicatorC6(),
			handleGetDetailedCitizensVaccinationsIndicatorC6(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC6() {
		setResumeIndicatorC6(
			await getResumeIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC6ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC6ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC6ByHealthTeam() {
		return (
			await getResumeIndicatorC6ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensScoresIndicatorC6(pageNumber?: number) {
		setIsLoadingCitizensScores(true);
		setPaginatedCitizensScores(
			await getDetailedCitizensScoresIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensScores(false);
	}

	async function handleGetAllDetailedCitizensScoresIndicatorC6() {
		return (
			await getDetailedCitizensScoresIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensWithAttendancesIndicatorC6(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedCitizensWithAttendances(
			await getDetailedCitizensAttendancesIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedCitizensWithAttendancesIndicatorC6() {
		return (
			await getDetailedCitizensAttendancesIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensAcsVisitsIndicatorC6(pageNumber?: number) {
		setIsLoadingCitizensAcsVisits(true);
		setPaginatedCitizensAcsVisits(
			await getDetailedCitizensAcsVisitsIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensAcsVisits(false);
	}

	async function handleGetAllDetailedCitizensAcsVisitsIndicatorC6() {
		return (
			await getDetailedCitizensAcsVisitsIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensVaccinationsIndicatorC6(pageNumber?: number) {
		setIsLoadingCitizensVaccinations(true);
		setPaginatedCitizensVaccinations(
			await getDetailedCitizensVaccinationsIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensVaccinations(false);
	}

	async function handleGetAllDetailedCitizensVaccinationsIndicatorC6() {
		return (
			await getDetailedCitizensVaccinationsIndicatorC6({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC6ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC6ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensScoresIndicatorC6(pageNumberCitizensScores);
	}, [pageNumberCitizensScores]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensScores === 1) {
				handleGetDetailedCitizensScoresIndicatorC6(pageNumberCitizensScores);
			} else {
				setPageNumberCitizensScores(1);
			}
		}
	}, [debouncedSearchCitizensScore, isEvaluatedCitizensScore]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensWithAttendancesIndicatorC6(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedCitizensWithAttendancesIndicatorC6(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance, isEvaluatedCitizensWithAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensAcsVisitsIndicatorC6(pageNumberCitizensAcsVisits);
	}, [pageNumberCitizensAcsVisits]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensAcsVisits === 1) {
				handleGetDetailedCitizensAcsVisitsIndicatorC6(pageNumberCitizensAcsVisits);
			} else {
				setPageNumberCitizensAcsVisits(1);
			}
		}
	}, [debouncedSearchCitizensAcsVisit, isEvaluatedCitizensAcsVisit]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensVaccinationsIndicatorC6(pageNumberCitizensVaccinations);
	}, [pageNumberCitizensVaccinations]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensVaccinations === 1) {
				handleGetDetailedCitizensVaccinationsIndicatorC6(pageNumberCitizensVaccinations);
			} else {
				setPageNumberCitizensVaccinations(1);
			}
		}
	}, [debouncedSearchCitizensVaccination, isEvaluatedCitizensVaccination]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C6 - Cuidado da Pessoa Idosa");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC6Resume resume={resumeIndicatorC6} />
			<IndicatorEsfC6HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC6ByHealthTeam={
					handleGetAllResumeIndicatorC6ByHealthTeam
				}
			/>
			<IndicatorEsfC6CitizensScores
				paginatedCitizensScores={paginatedCitizensScores}
				isLoading={isLoadingCitizensScores}
				search={searchCitizensScore}
				setSearch={setSearchCitizensScore}
				isEvaluated={isEvaluatedCitizensScore}
				setIsEvaluated={setIsEvaluatedCitizensScore}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensScores}
				setPageNumber={setPageNumberCitizensScores}
				handleGetAllDetailedCitizensScoresIndicatorC6={
					handleGetAllDetailedCitizensScoresIndicatorC6
				}
			/>
			<IndicatorEsfC6CitizensAttendances
				paginatedAttendances={paginatedCitizensWithAttendances}
				isLoading={isLoadingAttendances}
				search={searchCitizensWithAttendance}
				setSearch={setSearchCitizensWithAttendance}
				isEvaluated={isEvaluatedCitizensWithAttendance}
				setIsEvaluated={setIsEvaluatedCitizensWithAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC6={
					handleGetAllDetailedCitizensWithAttendancesIndicatorC6
				}
			/>
			<IndicatorEsfC6CitizensAcsVisits
				paginatedCitizensAcsVisits={paginatedCitizensAcsVisits}
				isLoading={isLoadingCitizensAcsVisits}
				search={searchCitizensAcsVisit}
				setSearch={setSearchCitizensAcsVisit}
				isEvaluated={isEvaluatedCitizensAcsVisit}
				setIsEvaluated={setIsEvaluatedCitizensAcsVisit}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensAcsVisits}
				setPageNumber={setPageNumberCitizensAcsVisits}
				handleGetAllDetailedCitizensAcsVisitsIndicatorC6={
					handleGetAllDetailedCitizensAcsVisitsIndicatorC6
				}
			/>
			<IndicatorEsfC6CitizensVaccinations
				paginatedCitizensVaccinations={paginatedCitizensVaccinations}
				isLoading={isLoadingCitizensVaccinations}
				search={searchCitizensVaccination}
				setSearch={setSearchCitizensVaccination}
				isEvaluated={isEvaluatedCitizensVaccination}
				setIsEvaluated={setIsEvaluatedCitizensVaccination}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensVaccinations}
				setPageNumber={setPageNumberCitizensVaccinations}
				handleGetAllDetailedCitizensVaccinationsIndicatorC6={
					handleGetAllDetailedCitizensVaccinationsIndicatorC6
				}
			/>
		</>
	);
}
